import React, { useState } from 'react'
import {
	Button,
	List,
	Modal,
	Form,
	Input,
	Tooltip,
	Tag,
	Space,
	Row,
	Col,
	Image,
	InputNumber,
	Typography
} from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { Comment, Comments } from '../../cls/utils/comments'
import { useStores } from '../../cls/stores/MobXProvider'
import { FormatedTimestamp, UserString } from '../Shared'
import { API_BASE_URL } from '../../config'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import UploadImage from '../Shared/UploadImage'
import { nextSortId } from '../../config/utils'
import { useSmartTranslation } from '../../cls/TranslationContext'

const { TextArea } = Input
/**
 * EditComment component renders a modal form to create or edit a comment.
 * It supports fields for sort ID, description, related link, and image uploads.
 * On submit, it collects the form values and uploaded file IDs, then invokes `onSave`.
 *
 * @component
 * @param {Object} props.comment – The existing Comment instance to edit, or undefined to add new.
 * @param {Function} props.onSave – Callback receiving form values when saving the comment.
 * @param {Function} props.getNextSortId – Function to compute the next sortId for new comments.
 * @param {Function} props.onCancel – Callback to close the modal without saving.
 * @returns {JSX.Element}
 */
const EditComment = observer(({ comment, onSave, getNextSortId, onCancel }) => {
	const { t } = useSmartTranslation()
	const { users } = useStores()
	const { loggedInUserId } = users

	const [isModalVisible, setIsModalVisible] = useState(true)
	const editingComment =
		comment ||
		new Comment({
			description: '',
			sortId: getNextSortId(),
			images: [],
			createdBy: loggedInUserId
		})

	const [fileList, setFileList] = useState(
		editingComment.images.map((image) => ({
			uid: image,
			name: image,
			status: 'done',
			url: `${API_BASE_URL}/images/uploads/${image}`
		})) || []
	)
	/**
	 * handleFileListChange updates the component’s `fileList` state when images are uploaded or removed.
	 * It also synchronizes the underlying Comment instance’s `images` array of file IDs.
	 *
	 * @function
	 * @private
	 * @param {Array<Object>} newFileList – AntD Upload fileList with { uid, name, status, url } entries.
	 */
	const handleFileListChange = (newFileList) => {
		setFileList(
			newFileList.map((file) => ({
				uid: file.uid,
				name: file.name,
				status: file.status,
				url: file.url
			}))
		)
		editingComment.images = newFileList.map((file) => file.uid)
	}

	return (
		<Modal
			title={editingComment ? t('__edit%_comment') : t('__add%_comment')}
			open={isModalVisible}
			onCancel={() => {
				setIsModalVisible(false)
				onCancel()
			}}
			footer={null}
		>
			<Form
				labelCol={{ span: 8 }}
				wrapperCol={{ span: 16 }}
				initialValues={editingComment || {}}
				onFinish={(values) => {
					const completeValues = values
					completeValues.images =
						fileList.map((file) => file.uid) || []
					onSave(completeValues)
				}}
			>
				<Form.Item
					label={t('single.sort-id')}
					name="sortId"
					rules={[
						{
							required: true,
							message: t('__enter.please%single.sort-id')
						}
					]}
				>
					<InputNumber min={1} />
				</Form.Item>
				<Form.Item
					label={t('single.description')}
					name="description"
					rules={[
						{
							required: true,
							message: t('__enter.please%_description')
						}
					]}
				>
					<TextArea rows={4} showCount maxLength={300} />
				</Form.Item>
				<Form.Item
					label={t('single.related-link')}
					name="web"
					rules={[
						{
							pattern: new RegExp(
								/^(https?):\/\/([\w.-]+|localhost)(:\d+)?(\/\S*)?$/
							),
							message: t('single.invalid.url')
						}
					]}
				>
					<Input />
				</Form.Item>

				<Form.Item label={t('single.upload-images')}>
					<UploadImage
						fileList={fileList}
						onFileListChange={handleFileListChange}
					/>
				</Form.Item>

				<Form.Item>
					<Button type="primary" htmlType="submit">
						{editingComment ? t('single.submit') : t('__add')}
					</Button>
				</Form.Item>
			</Form>
		</Modal>
	)
})
/**
 * CommentsInput component manages a list of comments, allowing add/edit/delete operations.
 * It renders a List of existing comments and uses EditComment in a modal for changes.
 *
 * @component
 * @param {string} props.createdBy – User ID to assign as the creator of new comments.
 * @param {Array<Object>} props.value – Initial array of comment data objects.
 * @param {Function} props.onChange – Callback receiving updated comment array after modifications.
 * @param {boolean} [props.disabled=false] – When true, disables add/edit/delete controls.
 * @returns {JSX.Element}
 */
const CommentsInput = observer(({ createdBy, value, onChange, ...props }) => {
	const { t } = useSmartTranslation()
	const { users } = useStores()
	const { loggedInUserId, loggedInUser } = users
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const { disabled } = props
	const comments = new Comments(value || [])
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [editingComment, setEditingComment] = useState(null)
	/**
	 * showModal opens the EditComment modal in “add new comment” mode (no existing comment loaded).
	 *
	 * @function
	 * @private
	 */
	const showModal = () => {
		setEditingComment(null)
		setIsModalVisible(true)
	}
	/**
	 * save writes the prepared list of comments back via onChange(comments.prepareForSave()).
	 *
	 * @function
	 * @private
	 */
	const save = () => {
		onChange(comments.prepareForSave())
	}
	/**
	 * getNextSortId returns the next sortId number for a new comment, computed from the current comment array.
	 *
	 * @function
	 * @private
	 * @returns {number}
	 */
	const getNextSortId = () => {
		return nextSortId(value)
	}
	/**
	 * onSaveComment handles the form submission from EditComment.
	 * - If editing, updates the existing Comment instance.
	 * - If new, adds a fresh Comment with description, sortId, link, images, and creator.
	 * Then calls save() and closes the modal.
	 *
	 * @function
	 * @private
	 * @param {Object} values – The form values { description, sortId, web, images }.
	 */
	const onSaveComment = (values) => {
		if (editingComment) {
			editingComment.description = values.description
			editingComment.sortId = values.sortId
			editingComment.web = values.web
			comments.updateComment(editingComment)
		} else {
			comments.addComment(
				values.description,
				values.sortId,
				values.web,
				values.images,
				createdBy
			)
		}
		save()
		setIsModalVisible(false)
	}
	/**
	 * handleEdit loads an existing comment into the modal for editing.
	 * Only the creator or a superAdmin may edit; otherwise shows an alert.
	 *
	 * @function
	 * @private
	 * @param {string} commentId – The ID of the comment to edit.
	 */
	const handleEdit = (commentId) => {
		const comment = comments.getCommentById(commentId)
		if (isSuperAdmin || loggedInUserId === comment.createdBy) {
			setEditingComment(comment)
			setIsModalVisible(true)
		} else {
			alert(t('__denied.edit'))
		}
	}
	/**
	 * handleDelete removes a comment by ID and calls save() to propagate changes.
	 * Only the creator or a superAdmin may delete; otherwise shows an alert.
	 *
	 * @function
	 * @private
	 * @param {string} commentId – The ID of the comment to delete.
	 */
	const handleDelete = (commentId) => {
		const comment = comments.getCommentById(commentId)
		if (isSuperAdmin || loggedInUserId === comment.createdBy) {
			comments.deleteComment(commentId)
			save()
		} else {
			alert(t('__denied.delete'))
		}
	}

	return (
		<>
			<List
				className="we-comments-input"
				header={
					!disabled && (
						<Button
							type="dashed"
							onClick={showModal}
							className="no-print"
						>
							{t('__add%_comment')}
						</Button>
					)
				}
				bordered
				dataSource={comments.sortedList}
				locale={{
					emptyText: t('single.no-data')
				}}
				renderItem={(comment) => (
					<List.Item
						actions={
							!disabled &&
							(isSuperAdmin ||
								loggedInUserId === comment.createdBy)
								? [
										<Button
											className="no-print"
											key={comment.id + 'edit'}
											type="link"
											onClick={() =>
												handleEdit(comment.id)
											}
										>
											<EditOutlined />
										</Button>,
										<Button
											className="no-print"
											key={comment.id + 'delete'}
											type="link"
											onClick={() =>
												handleDelete(comment.id)
											}
										>
											<DeleteOutlined />
										</Button>
								  ]
								: []
						}
					>
						<Space style={{ width: '100%' }} direction="vertical">
							<Row gutter={5} align="middle">
								<Col>
									{loggedInUserId === comment.createdBy ? (
										<Tooltip title={t('_comment.self')}>
											<Tag color="warning">
												<UserOutlined />
											</Tag>
										</Tooltip>
									) : (
										<Tooltip title={t('__x-by%_comment')}>
											<Tag color="default">
												<UserString
													userId={comment.createdBy}
													path="name"
												/>
											</Tag>
										</Tooltip>
									)}
								</Col>
								<Col
									style={{
										color: 'grey',
										fontSize: '12px'
									}}
								>
									<Tooltip title={t('single.created-on')}>
										<span>
											<FormatedTimestamp
												timestamp={comment.createdTs}
												formatedAs={t(
													'format.date-hour'
												)}
											/>
										</span>
									</Tooltip>
								</Col>
							</Row>
							<Typography.Text>
								{comment.sortId} -{' '}
								{comment.description
									.split('\n')
									.map((line, index) => (
										<span key={index}>
											{line}
											<br />
										</span>
									))}
							</Typography.Text>
							{comment?.images.length > 0 && (
								<div>
									{comment?.images &&
										comment?.images.map((imagePath) => (
											<Image
												key={imagePath}
												width={100}
												src={`${API_BASE_URL}/images/uploads/${imagePath}`}
											/>
										))}
								</div>
							)}
							{comment?.web && (
								<a
									className="no-print"
									href={comment?.web}
									target="_blank"
									rel="noreferrer"
								>
									{t('single.open-link')}
								</a>
							)}
						</Space>
					</List.Item>
				)}
			/>
			{isModalVisible && (
				<EditComment
					comment={editingComment}
					getNextSortId={getNextSortId}
					onSave={onSaveComment}
					onCancel={() => setIsModalVisible(false)}
				/>
			)}
		</>
	)
})

export default CommentsInput
